import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';

export type SortDirection = 'asc' | 'desc';

export interface TableSort {
  key: string;
  direction: SortDirection;
}

export interface ColumnDef<T> {
  key: string;
  label: string;
  render: (row: T) => React.ReactNode;
  sortValue?: (row: T) => string | number;
  /** The header sorts without a sortValue; only for a table whose sort is controlled. */
  sortable?: boolean;
  sx?: Record<string, unknown>;
}

interface SortableTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  rowKey: (row: T) => string;
  selectedKey?: string | null;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  emptyMessage?: string;
  defaultSortKey?: string;
  defaultSortDirection?: SortDirection;
  /** A controlled sort: the rows are shown as given, already sorted by the caller (a server-side sort). */
  sort?: TableSort;
  onSortChange?: (sort: TableSort) => void;
}

function SortableTable<T>({
  columns,
  data,
  rowKey,
  selectedKey,
  onRowClick,
  loading,
  emptyMessage = 'No data found',
  defaultSortKey,
  defaultSortDirection = 'desc',
  sort,
  onSortChange,
}: SortableTableProps<T>) {
  const [ownSortBy, setSortBy] = useState(defaultSortKey || columns[0]?.key || '');
  const [ownSortDirection, setSortDirection] = useState<SortDirection>(defaultSortDirection);
  const controlled = onSortChange !== undefined;
  const sortBy = controlled ? sort?.key ?? '' : ownSortBy;
  const sortDirection = controlled ? sort?.direction ?? defaultSortDirection : ownSortDirection;

  const handleSort = (key: string) => {
    let direction: SortDirection;
    if (sortBy === key) direction = sortDirection === 'asc' ? 'desc' : 'asc';
    else direction = key === defaultSortKey ? defaultSortDirection : 'asc';
    if (controlled) {
      onSortChange({ key, direction });
      return;
    }
    setSortBy(key);
    setSortDirection(direction);
  };

  const sortedData = useMemo(() => {
    if (controlled) return data;
    const col = columns.find(c => c.key === sortBy);
    if (!col?.sortValue) return data;
    return [...data].sort((a, b) => {
      const aVal = col.sortValue!(a);
      const bVal = col.sortValue!(b);
      const comparison = typeof aVal === 'number' && typeof bVal === 'number'
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [controlled, data, columns, sortBy, sortDirection]);

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.key}
                sortDirection={sortBy === col.key ? sortDirection : false}
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  ...col.sx,
                }}
              >
                {col.sortValue || (controlled && col.sortable) ? (
                  <TableSortLabel
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDirection : 'asc'}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row) => {
            const key = rowKey(row);
            return (
              <TableRow
                key={key}
                hover
                selected={selectedKey === key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((col) => (
                  <TableCell key={col.key}>{col.render(row)}</TableCell>
                ))}
              </TableRow>
            );
          })}
          {sortedData.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 6 }}>
                <Typography color="text.secondary">{emptyMessage}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SortableTable;
